import { useState, type RefObject } from 'react'

import { useFocusOnMount } from '~/lib/focus-on-mount'

/** How much a field like this takes, and no more — the spec's Asking the AI. */
export const MAX_CHARS = 500

const FIELD =
  'w-full rounded-2xl bg-surface-2 px-4 py-3 text-sm outline-none placeholder:text-ink-muted focus:ring-2 focus:ring-accent disabled:opacity-50'

/**
 * A field that takes five hundred characters and no more, saying how many are left while it is
 * the one being typed into. `focusOnMount` is the description's, which takes the cursor as the
 * tab opens.
 */
export function CountedTextarea({
  label,
  placeholder,
  rows,
  value,
  onValue,
  disabled = false,
  fieldRef,
  focusOnMount = false,
}: {
  label: string
  placeholder: string
  rows: number
  value: string
  onValue: (next: string) => void
  disabled?: boolean
  /** Handed the field, for a caller that gives it the cursor itself. */
  fieldRef?: RefObject<HTMLTextAreaElement | null> | undefined
  /** Or the field takes the cursor as it arrives, which is the same want said the other way. */
  focusOnMount?: boolean
}) {
  const mountedRef = useFocusOnMount<HTMLTextAreaElement>()
  const [typing, setTyping] = useState(false)
  return (
    <div className="min-w-0 flex-1 space-y-1">
      <textarea
        /* Both are answered where both are given: a ref handed in is never quietly dropped. */
        ref={(node) => {
          if (focusOnMount) {
            mountedRef.current = node
          }
          if (fieldRef !== undefined) {
            fieldRef.current = node
          }
        }}
        aria-label={label}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        maxLength={MAX_CHARS}
        value={value}
        onChange={(event) => {
          onValue(event.target.value)
        }}
        onFocus={() => {
          setTyping(true)
        }}
        onBlur={() => {
          setTyping(false)
        }}
        className={FIELD}
      />
      {typing && (
        <p className="text-right text-xs text-ink-muted">{String(MAX_CHARS - value.length)} left</p>
      )}
    </div>
  )
}
